<script setup lang="ts">
import type { ComputedRef } from 'vue';
import Button from 'primevue/button';
import Select from 'primevue/select';
import InputNumber from 'primevue/inputnumber';
import { DialogModal, useToaster } from '@tms-phoenix/ui-vue';
import { config } from '@/core/config';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import { fetchPatients } from '@/modules/patients/services/patients.api';
import { fetchMilkProvidersList } from '@/modules/mothers/services/milk-providers.api';
import { generateMilkBottleLabels } from '@/modules/mothers/services/milk-providers.api';
import { apiListItemToPatient } from '@/modules/patients/services/patient.transforms';
import { loadPatientLabelData, PATIENT_LABEL_TYPE } from '@/modules/patients/services/patient-label-data';
import { formatDateForDisplay } from '@/modules/mothers/services/milk-provider.transforms';
import { usePrinterSelect } from '@/shared/composables/usePrinterSelect';
import { submitPrintJob } from '@/shared/services/print-jobs.api';
import { useLabelPreviews, previewProps, type LabelPreviewRequest } from '@/shared/composables/useLabelPreviews';
import ZplLabelPreview from '@tms-phoenix/ui-vue/components/ui-library/ZplLabelPreview.vue';
import { logAuditEvent } from '@/core/services/audit.service';
import { isDonorScreeningBlocked } from '@/shared/utils/screening-status';
import { useLocale } from '@/shared/composables/useLocale';
import { useEnumTranslation } from '@/shared/composables/useEnumTranslation';
import PrinterSelect from '@/shared/components/ui/PrinterSelect.vue';
import PrintingOverlay from '@/shared/components/feedback/PrintingOverlay.vue';
import { useEntitlementsStore } from '@/stores';
import type { ApiPatientListItem, ApiPagination, Patient } from '@/modules/patients/types';
import type { ApiMilkProviderListItem } from '@/modules/mothers/types/milk-provider.types';

type TabKey = 'patient' | 'mother' | 'donor';
type ProviderRole = 'mother' | 'donor' | 'both';
type PrintPhase = 'selector' | 'printing';

type ProviderRecord = {
  id: string;
  name: string;
  mrn: string;
  dob: string;
  role: ProviderRole;
  screeningStatus: string;
  isActiveMother?: boolean | null;
};

type SelectOption = {
  label: string;
  value: string;
};

type DataState<T> = {
  items: T[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

const props = defineProps<{ visible: boolean }>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
}>();

const toast = useToaster();
const { t } = useLocale();
const { getTranslatedAllergenName, getAllergenSeverityLabel } = useEnumTranslation();
const { selectedPrinter, printerOptions, isDiscovering, discoverError, discoverAndSelect } = usePrinterSelect();
const entitlementsStore = useEntitlementsStore();

/*
 * Tab visibility — reuses AppSidebar.vue's exact entitlement/plan RBAC
 * evaluation (same entitlements store, same getters, same P1 package gate)
 * so a Print Label tab is never available for a feature the sidebar itself
 * would hide.
 */

// Mothers is not part of the P1 package — mirrors AppSidebar.vue's isPlanP1 gate.
const isPlanP1 = computed(() => entitlementsStore.planFlag === 'P1');

const canPrintPatientLabel = computed(() => entitlementsStore.hasEntitlement('PATIENT_MANAGEMENT'));
const canPrintMotherLabel = computed(() => entitlementsStore.hasEntitlement('MOTHER_MANAGEMENT') && !isPlanP1.value);
const canPrintDonorLabel = computed(() => entitlementsStore.hasEntitlement('DONOR_MANAGEMENT'));

const TAB_DEFS: { key: TabKey; labelKey: string }[] = [
  { key: 'patient', labelKey: 'common.patient' },
  { key: 'mother', labelKey: 'common.mother' },
  { key: 'donor', labelKey: 'common.donor' },
];

const TAB_VISIBILITY: Record<TabKey, ComputedRef<boolean>> = {
  patient: canPrintPatientLabel,
  mother: canPrintMotherLabel,
  donor: canPrintDonorLabel,
};

/** Print Label tabs filtered to only entitled ones, preserving the original order. */
const visibleTabs = computed(() => TAB_DEFS.filter((tab) => TAB_VISIBILITY[tab.key].value));


const MAX_LABELS = 10;

const activeTab = ref<TabKey>('patient');
const printPhase = ref<PrintPhase>('selector');

const selectedPatientId = ref<string | null>(null);
const selectedMotherId = ref<string | null>(null);
const selectedDonorId = ref<string | null>(null);

const numberOfLabels = ref(1);
const generatedBottleIds = ref<string[]>([]);
const isPrinting = ref(false);
const isGenerating = ref(false);

const patientState = reactive<DataState<Patient>>({
  items: [],
  loading: false,
  loaded: false,
  error: null,
});

const motherState = reactive<DataState<ProviderRecord>>({
  items: [],
  loading: false,
  loaded: false,
  error: null,
});

const donorState = reactive<DataState<ProviderRecord>>({
  items: [],
  loading: false,
  loaded: false,
  error: null,
});

const patientOptions = computed<SelectOption[]>(() =>
  patientState.items.map((p: Patient) => ({
    label: `${p.name} (${p.medicalRecordNumber})`,
    value: p.id,
  })),
);

const motherOptions = computed<SelectOption[]>(() =>
  motherState.items
    .filter((m: ProviderRecord) => (m.role === 'mother' || m.role === 'both') && m.isActiveMother !== false)
    .map((m: ProviderRecord) => ({
      label: `${m.name} (${m.mrn || t('common.nA')})`,
      value: m.id,
    })),
);

const donorOptions = computed<SelectOption[]>(() =>
  donorState.items
    .filter((d: ProviderRecord) => !isDonorScreeningBlocked(d.screeningStatus))
    .map((d: ProviderRecord) => ({
      label: `${d.name} (${d.mrn || t('common.nA')})`,
      value: d.id,
    })),
);

const selectedPatient = computed(() =>
  patientState.items.find((p: Patient) => p.id === selectedPatientId.value) ?? null,
);

const selectedMother = computed(() =>
  motherState.items.find((m: ProviderRecord) => m.id === selectedMotherId.value) ?? null,
);

const selectedDonor = computed(() =>
  donorState.items.find((d: ProviderRecord) => d.id === selectedDonorId.value) ?? null,
);

function resetSelectorState() {
  activeTab.value = visibleTabs.value[0]?.key ?? 'patient';
  selectedPatientId.value = null;
  selectedMotherId.value = null;
  selectedDonorId.value = null;
}

function extractListItems<T>(responseData: unknown): T[] {
  if (Array.isArray(responseData)) return responseData as T[];
  const env = responseData as { data?: T[] };
  return Array.isArray(env.data) ? env.data : [];
}

function extractPagination(response: unknown, responseData: unknown): ApiPagination | undefined {
  const fromResponse = (response as { pagination?: ApiPagination }).pagination;
  if (fromResponse) return fromResponse;
  if (Array.isArray(responseData)) return undefined;

  const env = responseData as {
    meta?: {
      pagination?: ApiPagination;
    };
  };

  return env.meta?.pagination;
}

function resolveProviderRole(role: string): ProviderRole {
  const normalized = role.toUpperCase();
  const hasMother = normalized.includes('MOTHER');
  const hasDonor = normalized.includes('DONOR');
  if (hasMother && hasDonor) return 'both';
  if (hasDonor) return 'donor';
  return 'mother';
}

async function loadPatientsList() {
  if (patientState.loading || patientState.loaded) return;

  patientState.loading = true;
  patientState.error = null;

  try {
    const allPatients: Patient[] = [];
    const seen = new Set<string>();
    const PAGE_SIZE = 100;
    const MAX_PAGES = 50;
    let page = 1;

    while (page <= MAX_PAGES) {
      const response = await fetchPatients({
        page,
        limit: PAGE_SIZE,
      });

      const items = extractListItems<ApiPatientListItem>(response.data);
      const pagination = extractPagination(response, response.data);

      for (const item of items) {
        if (seen.has(item.patient_id)) continue;
        seen.add(item.patient_id);
        allPatients.push(apiListItemToPatient(item));
      }

      if (!pagination?.hasNextPage || items.length === 0) {
        break;
      }

      page += 1;
    }

    patientState.items = allPatients.sort((a, b) => a.name.localeCompare(b.name));
    patientState.loaded = true;
  } catch {
    patientState.error = t('printCollection.failedLoad');
  } finally {
    patientState.loading = false;
  }
}

async function loadMilkProvidersList(target: 'mother' | 'donor') {
  const state = target === 'mother' ? motherState : donorState;
  if (state.loading || state.loaded) return;

  state.loading = true;
  state.error = null;

  try {
    const allProviders: ProviderRecord[] = [];
    const seen = new Set<string>();
    const PAGE_SIZE = 100;
    const MAX_PAGES = 50;
    let page = 1;

    while (page <= MAX_PAGES) {
      const response = await fetchMilkProvidersList({
        page,
        limit: PAGE_SIZE,
        role: target === 'mother' ? 'MOTHER_AND_BOTH' : 'DONOR_AND_BOTH',
      });

      const items = extractListItems<ApiMilkProviderListItem>(response.data);
      const pagination = extractPagination(response, response.data);

      for (const item of items) {
        if (seen.has(item.person_id)) continue;
        seen.add(item.person_id);

        allProviders.push({
          id: item.person_id,
          name: item.name,
          mrn: item.mrn ?? '',
          dob: formatDateForDisplay(item.dob ?? item.date_of_birth ?? ''),
          role: resolveProviderRole(item.role),
          screeningStatus: item.screening_status,
          isActiveMother: item.is_active_mother,
        });
      }

      if (!pagination?.hasNextPage || items.length === 0) {
        break;
      }

      page += 1;
    }

    state.items = allProviders.sort((a, b) => a.name.localeCompare(b.name));
    state.loaded = true;
  } catch {
    state.error = t('printCollection.failedLoad');
  } finally {
    state.loading = false;
  }
}

async function ensureActiveTabData() {
  if (activeTab.value === 'patient') {
    await loadPatientsList();
    return;
  }

  if (activeTab.value === 'mother') {
    await loadMilkProvidersList('mother');
    return;
  }

  await loadMilkProvidersList('donor');
}

/** The selected patient's label payload (SV-548), loaded when the printing phase opens. */
const patientLabelData = ref<Record<string, unknown> | null>(null);

function openPrintingPhase() {
  if (activeTab.value === 'patient' && !selectedPatient.value) return;
  if (activeTab.value === 'mother' && !selectedMother.value) return;
  if (activeTab.value === 'donor' && !selectedDonor.value) return;

  numberOfLabels.value = 1;
  generatedBottleIds.value = [];
  patientLabelData.value = null;
  printPhase.value = 'printing';
  // Patient: the label needs the detail record (allergies) and the reference data.
  const patientData = activeTab.value === 'patient' && selectedPatient.value
    ? loadPatientLabelData(selectedPatient.value, { allergenName: getTranslatedAllergenName, severity: getAllergenSeverityLabel })
      .then((data) => { patientLabelData.value = data; })
      .catch(() => undefined)
    : Promise.resolve();
  // Patient: preview now (after printer discovery). Mother/donor: clear until bottle IDs are generated.
  void Promise.all([discoverAndSelect().catch(() => undefined), patientData]).then(() => loadPreviews());
}

// ── Label payloads (shared by preview and print) and previews ───────────────

/** PATIENT_INFO_LABEL payload for the selected patient (buildPatientLabelData), or null while loading/failed. */
function buildPatientPrintData(): Record<string, unknown> | null {
  return patientLabelData.value ? { ...patientLabelData.value } : null;
}

/** MOTHER_DONOR_COLLECTION_LABELS payload for one bottle of the selected mother/donor. */
function buildCollectionPrintData(bottleId: string): Record<string, unknown> {
  const providerRecord = activeTab.value === 'mother' ? selectedMother.value : selectedDonor.value;
  return {
    role: activeTab.value === 'mother' ? t('printCollection.providerTypeMother') : t('printCollection.providerTypeDonor'),
    name: providerRecord?.name ?? '',
    mrn: providerRecord?.mrn ?? '',
    bottle_id: bottleId,
  };
}

/**
 * The labels the current tab will print: one patient label (copies repeat it),
 * or one collection label per generated bottle ID. Null = nothing to preview yet.
 */
function previewRequests(): LabelPreviewRequest[] | null {
  if (printPhase.value !== 'printing') return null;
  if (activeTab.value === 'patient') {
    const data = selectedPatient.value ? buildPatientPrintData() : null;
    return data ? [{ labelType: PATIENT_LABEL_TYPE, data }] : null;
  }
  if (generatedBottleIds.value.length === 0) return null;
  return generatedBottleIds.value.map((id) => ({ labelType: 'MOTHER_DONOR_COLLECTION_LABELS', data: buildCollectionPrintData(id) }));
}

const { previews, load, cancel: cancelPreviews } = useLabelPreviews({
  selectedPrinter,
  visible: () => props.visible && printPhase.value === 'printing',
});

function loadPreviews() {
  return load(previewRequests);
}

/** Drop any previews (and in-flight requests) from another tab or entity. */
function clearPreviews() {
  cancelPreviews();
  void load(() => null);
}

function handleModalClose() {
  emit('update:visible', false);
  clearPreviews();
  printPhase.value = 'selector';
  numberOfLabels.value = 1;
  generatedBottleIds.value = [];
  resetSelectorState();
}

async function generateBottleIdsForCollection() {
  if (!selectedMother.value && !selectedDonor.value) return;
  
  const count = Math.min(numberOfLabels.value, MAX_LABELS);
  isGenerating.value = true;
  let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
  let auditErrorCode: string | undefined;
  let auditErrorMessage: string | undefined;

  try {
    const providerRecord = activeTab.value === 'mother' ? selectedMother.value : selectedDonor.value;
    const response = await generateMilkBottleLabels({
      provider_id: providerRecord!.id,
      milk_provider_type: activeTab.value === 'donor' ? 'donor' : 'mother',
      no_of_bottles: count,
      tenant_id: config.tenantId,
    });
    const labels = response.data?.labels ?? [];
    generatedBottleIds.value = labels.map((l) => l.custom_label);
    auditStatus = 'SUCCESS';
    await loadPreviews();
  } catch (err: unknown) {
    toast.danger(getApiErrorMessage(err, 'printCollection.generateError'));
    auditStatus = 'FAILURE';
    auditErrorCode = 'GENERATE_COLLECTION_LABELS_FAILED';
    auditErrorMessage = err instanceof Error ? err.message : String(err);
  } finally {
    isGenerating.value = false;
    try {
      logAuditEvent({
        eventCategory: 'MOTHER_DONOR',
        eventType: 'GENERATE_COLLECTION_LABELS',
        eventStatus: auditStatus,
        resourceType: 'milkBottleLabels',
        resourceId: activeTab.value === 'mother' ? selectedMother.value?.id : selectedDonor.value?.id,
        isPhiAccess: true,
        newValue: {
          provider_id: activeTab.value === 'mother' ? selectedMother.value?.id : selectedDonor.value?.id,
          milk_provider_type: activeTab.value === 'donor' ? 'donor' : 'mother',
          no_of_bottles: count,
          tenant_id: config.tenantId,
          generated_count: generatedBottleIds.value.length,
        },
        errorCode: auditErrorCode,
        errorMessage: auditErrorMessage,
      });
    } catch {
      // Audit must never block the flow
    }
  }
}

async function handlePrint() {
  if (activeTab.value === 'patient') {
    await handlePrintPatientLabel();
  } else {
    await handlePrintCollectionLabel();
  }
}

async function handlePrintPatientLabel() {
  if (!selectedPatient.value || !selectedPrinter.value) return;
  const patientData = buildPatientPrintData();
  if (!patientData) {
    toast.danger(t('printJob.submitError'));
    return;
  }

  const count = Math.min(numberOfLabels.value, MAX_LABELS);
  isPrinting.value = true;
  let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
  let auditErrorCode: string | undefined;
  let auditErrorMessage: string | undefined;

  try {
    const data = [patientData];
    await submitPrintJob({
      labelType: PATIENT_LABEL_TYPE,
      data,
      copies: count,
      deviceId: selectedPrinter.value.value,
    });
    toast.success(t('printJob.submitSuccess', { count }));
    auditStatus = 'SUCCESS';
    handleModalClose();
  } catch {
    toast.danger(t('printJob.submitError'));
    auditStatus = 'FAILURE';
    auditErrorCode = 'PRINT_PATIENT_LABEL_FAILED';
    auditErrorMessage = 'Failed to print patient label';
  } finally {
    isPrinting.value = false;
    try {
      logAuditEvent({
        eventCategory: 'PATIENT',
        eventType: 'PRINT_LABEL',
        eventStatus: auditStatus,
        resourceType: 'patients',
        resourceId: selectedPatient.value?.id,
        isPhiAccess: true,
        newValue: {
          labelType: 'PATIENT_INFO_LABEL',
          copies: count,
          deviceId: selectedPrinter.value?.value,
          patientName: selectedPatient.value?.name,
          mrn: selectedPatient.value?.medicalRecordNumber,
        },
        errorCode: auditErrorCode,
        errorMessage: auditErrorMessage,
      });
    } catch {
      // Audit must never block the flow
    }
  }
}

async function handlePrintCollectionLabel() {
  if (!selectedPrinter.value || generatedBottleIds.value.length === 0) return;

  const providerRecord = activeTab.value === 'mother' ? selectedMother.value : selectedDonor.value;
  if (!providerRecord) return;

  isPrinting.value = true;
  let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
  let auditErrorCode: string | undefined;
  let auditErrorMessage: string | undefined;

  try {
    const data = generatedBottleIds.value.map(buildCollectionPrintData);
    await submitPrintJob({
      labelType: 'MOTHER_DONOR_COLLECTION_LABELS',
      data,
      deviceId: selectedPrinter.value.value,
    });
    toast.success(t('printJob.submitSuccess', { count: generatedBottleIds.value.length }));
    auditStatus = 'SUCCESS';
    handleModalClose();
  } catch {
    toast.danger(t('printJob.submitError'));
    auditStatus = 'FAILURE';
    auditErrorCode = 'PRINT_COLLECTION_LABELS_FAILED';
    auditErrorMessage = 'Failed to print mother/donor collection labels';
  } finally {
    isPrinting.value = false;
    try {
      logAuditEvent({
        eventCategory: 'MOTHER_DONOR',
        eventType: 'PRINT_LABEL',
        eventStatus: auditStatus,
        resourceType: 'milkBottleLabels',
        resourceId: providerRecord?.id,
        isPhiAccess: true,
        newValue: {
          labelType: 'MOTHER_DONOR_COLLECTION_LABELS',
          deviceId: selectedPrinter.value?.value,
          role: activeTab.value === 'mother' ? 'MOTHER' : 'DONOR',
          bottleIds: generatedBottleIds.value,
          name: providerRecord?.name,
          mrn: providerRecord?.mrn,
        },
        errorCode: auditErrorCode,
        errorMessage: auditErrorMessage,
      });
    } catch {
      // Audit must never block the flow
    }
  }
}

watch(
  () => props.visible,
  async (visible: boolean) => {
    if (!visible) {
      clearPreviews();
      printPhase.value = 'selector';
      return;
    }
    // Entitlements may have changed since the last time this modal was open —
    // fall back to the first entitled tab if the current one is no longer visible.
    if (!visibleTabs.value.some((tab) => tab.key === activeTab.value)) {
      activeTab.value = visibleTabs.value[0]?.key ?? 'patient';
    }
    await ensureActiveTabData();
  },
);

watch(
  () => activeTab.value,
  async (_newTab: TabKey, oldTab: TabKey) => {
    if (!props.visible) return;
    // Reset the previous tab's selection
    if (oldTab === 'patient') selectedPatientId.value = null;
    if (oldTab === 'mother') selectedMotherId.value = null;
    if (oldTab === 'donor') selectedDonorId.value = null;
    // Switching tabs collapses the print UI until a new entity is selected,
    // and drops the old tab's previews so none leak into the new one.
    clearPreviews();
    printPhase.value = 'selector';
    numberOfLabels.value = 1;
    generatedBottleIds.value = [];
    await ensureActiveTabData();
  },
);

watch(
  () => selectedPatientId.value,
  (id: string | null) => {
    if (!id || activeTab.value !== 'patient') return;
    openPrintingPhase();
  },
);

watch(
  () => selectedMotherId.value,
  (id: string | null) => {
    if (!id || activeTab.value !== 'mother') return;
    openPrintingPhase();
  },
);

watch(
  () => selectedDonorId.value,
  (id: string | null) => {
    if (!id || activeTab.value !== 'donor') return;
    openPrintingPhase();
  },
);
</script>

<template>
  <PrintingOverlay
    v-if="isPrinting"
    :message="t('printJob.submitting')"
  />

  <DialogModal
    :visible="visible"
    :title="t('dashboard.quickActionPrint')"
    max-width="max-w-2xl"
    :show-footer="false"
    :no-padding="true"
    @update:visible="handleModalClose"
    @close="handleModalClose"
  >
    <div class="flex flex-col gap-4 p-5">
      <!-- ── Tab bar ── -->
      <div
        class="grid gap-2 rounded-xl bg-gray-100 p-1"
        :style="{ gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))` }"
      >
        <button
          v-for="tab in visibleTabs"
          :key="tab.key"
          type="button"
          class="tab-button"
          :class="activeTab === tab.key ? 'tab-button-active' : 'tab-button-inactive'"
          @click="activeTab = tab.key"
        >
          {{ t(tab.labelKey) }}
        </button>
      </div>

      <!-- ── Entity selector ── -->
      <div class="flex flex-col gap-2">
        <template v-if="activeTab === 'patient'">
          <label class="text-sm font-medium text-gray-700">{{ t('common.selectPatient') }}</label>
          <Select
            v-model="selectedPatientId"
            :options="patientOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('printCollection.selectPatientPlaceholder')"
            filter
            class="w-full"
            :loading="patientState.loading"
          />
          <p
            v-if="patientState.loading"
            class="text-sm text-gray-500"
          >
            {{ t('printCollection.loadingPatients') }}
          </p>
          <p
            v-else-if="patientState.error"
            class="text-sm text-red-600"
          >
            {{ patientState.error }}
          </p>
          <p
            v-else-if="patientState.loaded && patientOptions.length === 0"
            class="text-sm text-gray-500"
          >
            {{ t('printCollection.noPatients') }}
          </p>
        </template>

        <template v-if="activeTab === 'mother'">
          <label class="text-sm font-medium text-gray-700">{{ t('printCollection.selectMotherLabel') }}</label>
          <Select
            v-model="selectedMotherId"
            :options="motherOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('printCollection.selectMotherPlaceholder')"
            filter
            class="w-full"
            :loading="motherState.loading"
          />
          <p
            v-if="motherState.loading"
            class="text-sm text-gray-500"
          >
            {{ t('patient.loadingMothers') }}
          </p>
          <p
            v-else-if="motherState.error"
            class="text-sm text-red-600"
          >
            {{ motherState.error }}
          </p>
          <p
            v-else-if="motherState.loaded && motherOptions.length === 0"
            class="text-sm text-gray-500"
          >
            {{ t('printCollection.noMothers') }}
          </p>
        </template>

        <template v-if="activeTab === 'donor'">
          <label class="text-sm font-medium text-gray-700">{{ t('patient.selectDonor') }}</label>
          <Select
            v-model="selectedDonorId"
            :options="donorOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('patient.selectADonor')"
            filter
            class="w-full"
            :loading="donorState.loading"
          />
          <p
            v-if="donorState.loading"
            class="text-sm text-gray-500"
          >
            {{ t('patient.loadingDonors') }}
          </p>
          <p
            v-else-if="donorState.error"
            class="text-sm text-red-600"
          >
            {{ donorState.error }}
          </p>
          <p
            v-else-if="donorState.loaded && donorOptions.length === 0"
            class="text-sm text-gray-500"
          >
            {{ t('printCollection.noDonors') }}
          </p>
        </template>
      </div>

      <!-- ── Print UI: appended below once Continue is clicked ── -->
      <template v-if="printPhase === 'printing'">
        <div class="h-px bg-gray-200" />

        <!-- PATIENT: info description + printer/labels row + label preview -->
        <template v-if="activeTab === 'patient'">
          <p class="text-sm text-primary-700 font-medium leading-relaxed">
            {{ t('patients.printLabel.infoTitle') }}: {{ t('patients.printLabel.infoDesc') }}
          </p>

          <div class="grid grid-cols-2 gap-4">
            <PrinterSelect
              v-model="selectedPrinter"
              :options="printerOptions"
              :is-discovering="isDiscovering"
              :discover-error="discoverError"
            />
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">{{ t('printCollection.numberOfLabels') }}</label>
              <InputNumber
                v-model="numberOfLabels"
                :min="1"
                :max="MAX_LABELS"
                show-buttons
                button-layout="horizontal"
                class="w-full"
                input-class="text-center"
              />
              <p class="text-xs text-gray-400">
                {{ t('printCollection.printMultipleNote') }}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <p class="text-sm font-medium text-gray-700">
              {{ t('patients.printLabel.labelPreview') }}
            </p>
            <ZplLabelPreview
              v-for="(preview, index) in previews"
              :key="index"
              v-bind="previewProps(preview)"
            />
          </div>
        </template>

        <!-- MOTHER / DONOR: info card + printer/labels row + generate bottle IDs -->
        <template v-else>
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p class="text-sm font-semibold text-gray-800 mb-2">
              {{ activeTab === 'mother' ? t('printCollection.motherInformation') : t('donor.tabs.donorInformation') }}
            </p>
            <div class="grid grid-cols-2 gap-y-1 text-sm">
              <div>
                <span class="text-gray-500">{{ t('common.name') }}</span>
                <p class="font-medium text-gray-900 mt-0.5">
                  {{ activeTab === 'mother' ? selectedMother?.name : selectedDonor?.name }}
                </p>
              </div>
              <div>
                <span class="text-gray-500">{{ activeTab === 'mother' ? t('printCollection.motherIdLabel') : t('donor.donorId') }}</span>
                <p class="font-medium text-gray-900 mt-0.5">
                  {{ activeTab === 'mother' ? selectedMother?.mrn : selectedDonor?.mrn }}
                </p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <PrinterSelect
              v-model="selectedPrinter"
              :options="printerOptions"
              :is-discovering="isDiscovering"
              :discover-error="discoverError"
            />
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">{{ t('printCollection.numberOfLabels') }}</label>
              <InputNumber
                v-model="numberOfLabels"
                :min="1"
                :max="MAX_LABELS"
                show-buttons
                button-layout="horizontal"
                class="w-full"
                input-class="text-center"
              />
              <p class="text-xs text-gray-400">
                {{ t('printCollection.printMultipleNote') }}
              </p>
            </div>
          </div>

          <Button
            :label="t('printCollection.generateBottleIds')"
            severity="secondary"
            outlined
            class="w-full rounded-xl!"
            :loading="isGenerating"
            :disabled="isGenerating"
            @click="generateBottleIdsForCollection"
          />

          <div
            v-if="generatedBottleIds.length > 0"
            class="flex flex-col gap-3"
          >
            <p class="text-sm font-semibold text-primary-700">
              {{ t('printCollection.generatedIds', { count: generatedBottleIds.length }) }}
            </p>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="id in generatedBottleIds"
                :key="id"
                class="rounded-md border border-primary-200 bg-primary-50 px-3 py-1 text-sm font-mono text-primary-800"
              >
                {{ id }}
              </span>
            </div>

            <!-- Label Preview grid -->
            <p class="text-sm font-semibold text-gray-700">
              {{ t('printCollection.labelPreview') }}
            </p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1">
              <ZplLabelPreview
                v-for="(preview, index) in previews"
                :key="generatedBottleIds[index] ?? index"
                v-bind="previewProps(preview)"
              />
            </div>
          </div>
        </template>
      </template>

      <!-- ── Footer ── -->
      <div class="flex justify-between gap-2 border-t border-gray-200 pt-4">
        <Button
          :label="t('common.cancel')"
          severity="secondary"
          outlined
          class="rounded-xl! px-4 py-2"
          :disabled="isPrinting"
          @click="handleModalClose"
        />

        <Button
          v-if="printPhase === 'printing'"
          :label="activeTab === 'patient' ? t('patients.printLabel.title') : t('dashboard.quickActionPrint')"
          severity="primary"
          class="rounded-xl! px-4 py-2"
          :disabled="
            !selectedPrinter ||
              isPrinting ||
              (activeTab !== 'patient' && generatedBottleIds.length === 0)
          "
          :loading="isPrinting"
          @click="handlePrint"
        />
      </div>
    </div>
  </DialogModal>
</template>

<style scoped>
.tab-button {
  border-radius: 0.625rem;
  border: none;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.tab-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.tab-button-active {
  background: #ffffff;
  color: #1d4ed8;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.tab-button-inactive {
  background: transparent;
  color: #64748b;
}
</style>
